import * as React from 'react';
import { useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { Menu, Avatar, Icon, icons, useDialog } from '@agentisai/design-system';
import { useAuth } from '../auth/AuthProvider';
import { useViewportTier } from '../hooks/useViewportTier';
import { useUsage } from '../queries/useUsage';
import { useCan } from '../queries/useMe';
import { formatCreditsValue } from '../lib/formatCredits';
import {
  trigger as triggerCls,
  backdrop as backdropCls,
  sheetWrap as sheetWrapCls,
  sheet as sheetCls,
  dragHandle as dragHandleCls,
  sheetHeader as sheetHeaderCls,
  sheetName as sheetNameCls,
  sheetMeta as sheetMetaCls,
  sheetSection as sheetSectionCls,
  sheetItem as sheetItemCls,
  sheetItemIcon as sheetItemIconCls,
  sheetItemHint as sheetItemHintCls,
} from './UserMenu.css';

/**
 * The D13-08 usage-line value, derived ONCE from the shared D13-07 query and
 * fed to both tiers (regular popover `hint` + compact sheet trailing span) so
 * they cannot disagree. `'…'` while loading; `undefined` on error OR an
 * unpriced share (D13-03's null discipline — never a fake `0`/`$0.00`); the
 * menu's job is Sign Out, so a failed/unpriced fetch degrades quietly rather
 * than showing an error affordance.
 */
function useUsageHint(): string | undefined {
  const query = useUsage();
  if (query.isLoading) return '…';
  const credits = query.data?.data.you.credits;
  return credits === undefined ? undefined : formatCreditsValue(credits);
}

/**
 * UserMenu (S-H2) — the top-right avatar that opens a profile / account
 * menu with a working Sign Out, rendering the REAL signed-in claims.
 *
 * Lives in `apps/ui/web` (not the DS) because it couples to `useAuth()` — the
 * Auth0 client boundary (frontend-architecture R0 + R13). It is pure
 * COMPOSITION of DS primitives: the regular-tier popover is the DS `Menu`
 * (with its `header` slot + `section` item + `Menu.Header`, shipped in
 * S-H1); the compact-tier (<768px) bottom-sheet is a `useDialog`-backed
 * surface mirroring the DS `Modal` bottom-sheet shape (decision 7a —
 * app-local, not promoted to the DS).
 *
 * The two presentations share the SAME identity (name + email) and the
 * SAME actions (Profile — navigates to the D13-07 Usage view; Agent evals —
 * staff-only, ADR-0072, shown only when `/me` grants `evals:read`; Sign
 * Out — calls `logout()`), derived once in render (no `useMemo` — the
 * React Compiler memoizes; R2/R11).
 */
export function UserMenu() {
  const { user, tenantBrand, logout } = useAuth();
  const tier = useViewportTier();
  const navigate = useNavigate();
  const usageHint = useUsageHint();
  // Staff-only (ADR-0072). Hides the item; the /evals API re-checks the grant.
  const canSeeEvals = useCan('evals:read');

  // Real claims (never the prototype's "Jaco Vorster / JV"). The Avatar
  // derives initials from `fullName`; the email line is hidden when absent.
  const fullName = user?.name ?? 'User';
  const email = user?.email ?? '';
  const tint = tenantBrand?.avatarTint;

  // The avatar is a non-focusable <span role="img"> — wrap it in a real
  // <button> so the trigger is keyboard-operable and carries the accessible
  // name + aria-haspopup/expanded (the DS Menu/Popover wire those on it).
  const avatarTrigger = (
    <button type="button" className={triggerCls} aria-label={`Account menu for ${fullName}`}>
      <Avatar name={fullName} size="sm" style={tint ? { background: tint } : undefined} />
    </button>
  );

  if (tier === 'compact') {
    return (
      <CompactUserMenu
        trigger={avatarTrigger}
        fullName={fullName}
        email={email}
        usageHint={usageHint}
        onProfile={() => void navigate('/profile')}
        onEvals={canSeeEvals ? () => void navigate('/evals') : undefined}
        onSignOut={logout}
      />
    );
  }

  return (
    <Menu
      aria-label="Account"
      placement="bottom-end"
      minWidth={240}
      trigger={avatarTrigger}
      header={<Menu.Header name={fullName} email={email} />}
      items={[
        {
          id: 'usage',
          label: 'Usage',
          icon: <Icon as={icons.Zap} size={16} />,
          hint: usageHint,
          onSelect: () => void navigate('/profile'),
        },
        { kind: 'section', id: 'account', label: 'Account' },
        {
          id: 'profile',
          label: 'Profile',
          icon: <Icon as={icons.User} size={16} />,
          onSelect: () => void navigate('/profile'),
        },
        ...(canSeeEvals
          ? [
              {
                id: 'evals',
                label: 'Agent evals',
                icon: <Icon as={icons.ShieldCheck} size={16} />,
                onSelect: () => void navigate('/evals'),
              },
            ]
          : []),
        {
          id: 'signout',
          label: 'Sign Out',
          icon: <Icon as={icons.CornerDownLeft} size={16} />,
          onSelect: () => logout(),
        },
      ]}
    />
  );
}
UserMenu.displayName = 'UserMenu';

interface CompactUserMenuProps {
  trigger: React.ReactElement<{ onClick?: React.MouseEventHandler<HTMLElement> }>;
  fullName: string;
  email: string;
  usageHint: string | undefined;
  onProfile: () => void;
  /** Staff-only (ADR-0072); absent → the row is not rendered. */
  onEvals?: () => void;
  onSignOut: () => void;
}

/**
 * Compact bottom-sheet (<768px). A `role="dialog"` surface delegating focus
 * trap + Escape + scroll-lock + focus-restore to `useDialog` (the DS hook —
 * frontend-architecture R7), sharing the same identity + actions as the
 * desktop popover. Slides up honouring `prefers-reduced-motion`; ≥44px rows.
 */
function CompactUserMenu({
  trigger,
  fullName,
  email,
  usageHint,
  onProfile,
  onEvals,
  onSignOut,
}: CompactUserMenuProps) {
  const [open, setOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dialogTitleId = useId();

  const close = () => setOpen(false);

  useDialog({
    open,
    containerRef: sheetRef,
    onClose: close,
    // Pointer-opened sheet may leave focus on <body>; restore to the avatar.
    returnFocusRef: triggerRef,
  });

  // Clone the shared avatar trigger to own its ref + click-to-open, so the
  // trigger element is identical across both tiers.
  const triggerNode = React.cloneElement(
    trigger as React.ReactElement<{
      ref?: React.Ref<HTMLButtonElement>;
      onClick?: React.MouseEventHandler<HTMLElement>;
      'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
      'aria-expanded'?: boolean;
    }>,
    {
      ref: triggerRef,
      onClick: () => setOpen(true),
      'aria-haspopup': 'dialog',
      'aria-expanded': open,
    },
  );

  const runAndClose = (fn: () => void) => () => {
    fn();
    close();
  };

  return (
    <>
      {triggerNode}
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <div className={backdropCls} onClick={close} aria-hidden="true" />
            <div className={sheetWrapCls}>
              <div
                ref={sheetRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={dialogTitleId}
                tabIndex={-1}
                className={sheetCls}
              >
                <div className={dragHandleCls} aria-hidden="true" />
                <div className={sheetHeaderCls}>
                  <span id={dialogTitleId} className={sheetNameCls}>
                    {fullName}
                  </span>
                  {email !== '' && (
                    <span className={sheetMetaCls} title={email}>
                      {email}
                    </span>
                  )}
                </div>
                <button type="button" className={sheetItemCls} onClick={runAndClose(onProfile)}>
                  <span className={sheetItemIconCls}>
                    <Icon as={icons.Zap} size={16} />
                  </span>
                  Usage
                  {usageHint !== undefined && <span className={sheetItemHintCls}>{usageHint}</span>}
                </button>
                <span className={sheetSectionCls}>Account</span>
                <button type="button" className={sheetItemCls} onClick={runAndClose(onProfile)}>
                  <span className={sheetItemIconCls}>
                    <Icon as={icons.User} size={16} />
                  </span>
                  Profile
                </button>
                {onEvals && (
                  <button type="button" className={sheetItemCls} onClick={runAndClose(onEvals)}>
                    <span className={sheetItemIconCls}>
                      <Icon as={icons.ShieldCheck} size={16} />
                    </span>
                    Agent evals
                  </button>
                )}
                <button type="button" className={sheetItemCls} onClick={runAndClose(onSignOut)}>
                  <span className={sheetItemIconCls}>
                    <Icon as={icons.CornerDownLeft} size={16} />
                  </span>
                  Sign Out
                </button>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
CompactUserMenu.displayName = 'CompactUserMenu';
